import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { billingAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  ArrowLeft, Crown, Check, Loader2, Disc3, Sun, Moon, 
  Zap, Download, Scan, Camera, Infinity, AlertCircle,
  CreditCard, X, Tag, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

export default function Upgrade() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [stripeConfigured, setStripeConfigured] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);

  // Check for success/cancel from Stripe redirect
  useEffect(() => {
    const upgradeStatus = searchParams.get('upgrade');
    if (upgradeStatus === 'success') {
      toast.success('Welcome to PRO! Your upgrade is complete.');
      refreshUser?.();
      navigate('/', { replace: true });
    } else if (upgradeStatus === 'canceled') {
      toast.info('Upgrade canceled');
      navigate('/upgrade', { replace: true });
    }
  }, [searchParams, navigate, refreshUser]);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await billingAPI.getPricing();
        setPlans(res.data.plans);
        setStripeConfigured(res.data.stripe_configured);
      } catch (err) {
        toast.error('Could not fetch pricing information');
      } finally {
        setLoading(false);
      }
    };
    fetchPricing();
  }, []);

  // Fetch subscription status for PRO users
  useEffect(() => {
    const fetchSubscription = async () => {
      if (user?.plan === 'PRO' || user?.plan === 'LIFETIME_PRO') {
        try {
          const res = await billingAPI.getSubscription();
          setSubscription(res.data);
        } catch (err) {
          console.error('Could not fetch subscription');
        }
      }
    };
    fetchSubscription();
  }, [user]);

  const handleUpgrade = async (planId) => {
    if (planId === 'FREE') return;
    
    setCheckoutLoading(planId);
    
    try {
      const res = await billingAPI.createCheckout(planId);
      
      if (!res.data.stripe_configured) {
        toast.error(res.data.message || 'Stripe is not configured yet');
        setStripeConfigured(false);
        return;
      }
      
      setStripeConfigured(true);
      
      if (res.data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = res.data.checkout_url;
      } else {
        toast.error('Could not create checkout session');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'An error occurred');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleChangePlan = async (newPlanId) => {
    if (!subscription?.stripe_subscription_id) {
      toast.error('Kontakt support for at skifte plan');
      return;
    }
    
    setCheckoutLoading(newPlanId);
    
    try {
      const res = await billingAPI.changePlan(newPlanId);
      
      if (res.data.success) {
        toast.success(res.data.message || 'Plan changed!');
        // Refresh subscription info
        const subRes = await billingAPI.getSubscription();
        setSubscription(subRes.data);
        refreshUser?.();
      } else {
        toast.error(res.data.message || 'Could not change plan');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Der opstod en fejl');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your PRO subscription? You will keep access until the end of your billing period.')) {
      return;
    }
    
    setCancelLoading(true);
    try {
      const res = await billingAPI.cancelSubscription();
      if (res.data.success) {
        toast.success('Your subscription has been cancelled. You keep PRO access until the end of your billing period.');
        setSubscription({ ...subscription, cancel_at_period_end: true });
      } else {
        toast.error(res.data.message || 'Could not cancel subscription');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not cancel subscription');
    } finally {
      setCancelLoading(false);
    }
  };

  const isPro = user?.plan === 'PRO' || user?.plan === 'LIFETIME_PRO';
  const isLifetime = user?.plan === 'LIFETIME_PRO';

  const getFeatureIcon = (feature) => {
    if (feature.includes('Unlimited')) return <Infinity className="w-4 h-4 text-primary" />;
    if (feature.includes('stregkode') || feature.includes('Scan')) return <Scan className="w-4 h-4 text-primary" />;
    if (feature.includes('AI')) return <Camera className="w-4 h-4 text-primary" />;
    if (feature.includes('Eksport') || feature.includes('Export')) return <Download className="w-4 h-4 text-primary" />;
    if (feature.includes('Save')) return <Zap className="w-4 h-4 text-green-500" />;
    if (feature.includes('Sale list') || feature.includes('Salgs')) return <Tag className="w-4 h-4 text-primary" />;
    return <Check className="w-4 h-4 text-primary" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="gap-2"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <img src="/logo-icon.svg" alt="Vinyllog" className="w-8 h-8" />
              <div className="flex flex-col">
                <span className="font-bold leading-tight">Vinyllog</span>
                <span className="text-[9px] text-muted-foreground tracking-wide hidden sm:block">Catalog Your Collection</span>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="w-9 h-9"
            >
              {theme === 'dark' ? (
                <Sun className="w-4 h-4 text-primary" />
              ) : (
                <Moon className="w-4 h-4 text-primary" />
              )}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-4 md:px-8 py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-primary/20 text-primary border-primary/30">
            <Crown className="w-3 h-3 mr-1" />
            PRO
          </Badge>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            Get more control over your collection
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            PRO gives you batch import, export and advanced search – to save time and get deeper insights into your vinyl collection.
          </p>
        </div>

        {/* Already PRO message */}
        {isPro && (
          <div className="mb-8 max-w-xl mx-auto">
            <Alert className="bg-green-500/10 border-green-500/30">
              <Crown className="h-4 w-4 text-green-500" />
              <AlertDescription className="text-green-500">
                {isLifetime ? (
                  "You have LIFETIME PRO! Thanks for your support – you have access to all PRO features forever."
                ) : (
                  <>
                    You have PRO! Thanks for your support.
                    {subscription?.cancel_at_period_end && (
                      <span className="block mt-1 text-yellow-500">
                        Your subscription will end at the end of the current billing period.
                      </span>
                    )}
                  </>
                )}
              </AlertDescription>
            </Alert>
            
            {isPro && !isLifetime && !subscription?.cancel_at_period_end && (
              <div className="mt-4 text-center">
                {subscription?.has_subscription ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCancelSubscription}
                    disabled={cancelLoading}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    {cancelLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <X className="w-4 h-4 mr-2" />
                    )}
                    Cancel subscription
                  </Button>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Kontakt support for at administrere dit abonnement.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Stripe not configured warning */}
        {!stripeConfigured && stripeConfigured !== null && (
          <Alert className="mb-8 max-w-xl mx-auto bg-yellow-500/10 border-yellow-500/30">
            <AlertCircle className="h-4 w-4 text-yellow-500" />
            <AlertDescription>
              <strong>Demo mode:</strong> Stripe payments are not configured yet. 
              Add STRIPE_SECRET_KEY to backend/.env to enable payments.
            </AlertDescription>
          </Alert>
        )}

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {plans.map((plan) => {
            const isFree = plan.id === 'FREE';
            const isYearly = plan.id === 'PRO_YEARLY';
            const isCurrentPlan = (isFree && !isPro) || (isPro && !isFree);
            
            return (
              <Card 
                key={plan.id}
                className={`relative overflow-hidden transition-all ${
                  isYearly 
                    ? 'border-primary shadow-lg shadow-primary/10 scale-[1.02]' 
                    : 'border-border'
                }`}
                data-testid={`plan-card-${plan.id}`}
              >
                {isYearly && (
                  <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-xs px-3 py-1 rounded-bl-lg font-medium">
                    Most popular
                  </div>
                )}
                
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2">
                    {!isFree && <Crown className="w-5 h-5 text-primary" />}
                    {plan.name}
                  </CardTitle>
                  <div className="mt-2">
                    <span className="text-3xl font-bold">
                      {plan.price === 0 ? 'Free' : `$${plan.price}`}
                    </span>
                    {plan.price > 0 && (
                      <span className="text-muted-foreground text-sm">
                        /{plan.interval === 'month' ? 'mo' : 'yr'}
                      </span>
                    )}
                  </div>
                  {isYearly && (
                    <Badge variant="secondary" className="w-fit mt-2 bg-green-500/10 text-green-500 border-green-500/30">
                      Save $3.89/year
                    </Badge>
                  )}
                </CardHeader>
                
                <CardContent className="pb-6">
                  <ul className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        {getFeatureIcon(feature)}
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                
                <CardFooter>
                  {isFree ? (
                    <Button 
                      variant="outline" 
                      className="w-full" 
                      disabled
                    >
                      {isPro ? 'Previous plan' : 'Current plan'}
                    </Button>
                  ) : isPro && !isLifetime ? (
                    // Show switch plan button for PRO users (not lifetime)
                    subscription?.stripe_subscription_id ? (
                      <Button 
                        variant="outline" 
                        className="w-full gap-2"
                        onClick={() => handleChangePlan(plan.id)}
                        disabled={checkoutLoading !== null}
                        data-testid={`switch-plan-btn-${plan.id}`}
                      >
                        {checkoutLoading === plan.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <RefreshCw className="w-4 h-4" />
                            Switch to {plan.interval === 'month' ? 'monthly' : 'yearly'}
                          </>
                        )}
                      </Button>
                    ) : (
                      <Button 
                        variant="outline" 
                        className="w-full" 
                        disabled
                      >
                        <Check className="w-4 h-4 mr-2" />
                        PRO active
                      </Button>
                    )
                  ) : isLifetime ? (
                    <Button 
                      variant="outline" 
                      className="w-full" 
                      disabled
                    >
                      <Check className="w-4 h-4 mr-2" />
                      LIFETIME aktiv
                    </Button>
                  ) : (
                    <Button
                      className={`w-full gap-2 ${
                        isYearly 
                          ? 'bg-primary text-primary-foreground hover:bg-primary/90' 
                          : ''
                      }`}
                      variant={isYearly ? 'default' : 'outline'}
                      onClick={() => handleUpgrade(plan.id)}
                      disabled={checkoutLoading !== null}
                      data-testid={`upgrade-btn-${plan.id}`}
                    >
                      {checkoutLoading === plan.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4" />
                          Upgrade now
                        </>
                      )}
                    </Button>
                  )}
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {/* FAQ / Trust section */}
        <div className="mt-16 text-center">
          <h2 className="text-xl font-semibold mb-4">Frequently Asked Questions</h2>
          <div className="max-w-2xl mx-auto grid gap-4 text-left">
            <Card className="bg-card/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Can I cancel anytime?</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                Yes! You can cancel your subscription anytime. You keep PRO access 
                until the end of your billing period.
              </CardContent>
            </Card>
            <Card className="bg-card/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">What payment methods are accepted?</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                We accept all major credit cards (Visa, Mastercard, American Express) 
                as well as Apple Pay and Google Pay via Stripe.
              </CardContent>
            </Card>
            <Card className="bg-card/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">What happens to my collection if I downgrade?</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                Your collection remains completely intact – you never lose your data. 
                You just lose access to PRO features like batch import, export and matrix search.
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
